'use client';

import { IconX, IconHome } from '@tabler/icons-react';
import type { MouseEventHandler, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactDOMServer from 'react-dom/server';

export type ModalProps = {
	modalId: string;
	className?: string;
	closeOnClickOutside?: boolean;
	closeOnEsc?: boolean;
	children?: ReactNode;
	title?: ReactNode;
	onClose?: (modalId: string) => void;
	showClose?: boolean;
	homeHref?: string;
};

export type ModalImperativeRef = {
	close: () => void;
	setTitle: (title: ReactNode) => void;
};

export default forwardRef<ModalImperativeRef, ModalProps>(function Modal(props, ref) {
	const { className, children, modalId, title, onClose, closeOnClickOutside, closeOnEsc, homeHref } = props;
	const showClose = props.showClose ?? true;

	const elRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	const closeModal = useCallback(() => {
		if (elRef.current) {
			elRef.current.classList.add('closing');
			setTimeout(() => {
				onClose?.(modalId);
			}, 60);
		} else {
			onClose?.(modalId);
		}
	}, [onClose, modalId]);

	const handleHomeClicked: MouseEventHandler = (e) => {
		e.preventDefault();
		if (homeHref) {
			window.location.assign(homeHref);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			close: closeModal,
			setTitle: (title: ReactNode) => {
				const modal = elRef.current;
				if (modal) {
					const header = modal.querySelector('.header h4');
					if (header) {
						header.innerHTML = ReactDOMServer.renderToStaticMarkup(<>{title}</>);
					}
				}
			},
		}),
		[closeModal],
	);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (closeOnEsc && e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				closeModal();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [closeModal, closeOnEsc]);

	const handleOverlayClicked: MouseEventHandler<HTMLDivElement> = (e) => {
		if (closeOnClickOutside) {
			e.preventDefault();
			e.stopPropagation();
			closeModal();
		}
	};

	const handleCloseButtonClicked: MouseEventHandler<SVGSVGElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		closeModal();
	};

	return (
		<div ref={elRef} id={modalId} className={`Modal ${className ?? ''}`}>
			<div className="overlay" onClick={handleOverlayClicked}></div>
			<div className="inner light-bg">
				<div className="content">
					{(title || showClose || homeHref) && (
						<div className="header">
							<h4>{title}</h4>
							{homeHref && <IconHome className="home-btn" onClick={handleHomeClicked} stroke={1.5} />}
							{showClose && (
								<IconX className="close-btn" onClick={handleCloseButtonClicked} stroke={1.5} />
							)}
						</div>
					)}
					{children ? <div className="body">{children}</div> : null}
				</div>
			</div>
		</div>
	);
});
